import moment from "moment/moment";
import { buildGoogleMapsUrl } from "utils/mapUtils";

export const SHIPPING_NOTE_API_STATUSES = [
  "CREATED",
  "ARRIVED",
  "FULFILLED",
  "PARTIALLY_FULFILLED",
  "NO_SHOW",
];

export const SHIPPING_NOTE_FILTERS = [
  { id: "dispatched", label: "Created", statuses: ["CREATED"] },
  { id: "arrived", label: "Arrived", statuses: ["ARRIVED"] },
  {
    id: "fulfilled",
    label: "Fulfilled",
    statuses: ["FULFILLED", "PARTIALLY_FULFILLED"],
  },
  { id: "no_show", label: "No show", statuses: ["NO_SHOW"] },
  { id: "all", label: "All", statuses: SHIPPING_NOTE_API_STATUSES },
];

const SHIPPING_NOTE_STATUS_LABELS = {
  CREATED: "Created",
  ARRIVED: "Arrived",
  FULFILLED: "Fulfilled",
  PARTIALLY_FULFILLED: "Partially fulfilled",
  CANCELLED: "Cancelled",
  DELETED: "Cancelled",
  NO_SHOW: "No show",
};

export const formatShippingNoteStatus = (status) =>
  SHIPPING_NOTE_STATUS_LABELS[String(status || "").toUpperCase()] || "—";

const normalizeText = (value) =>
  value === null || value === undefined
    ? ""
    : String(value).trim().toLowerCase();

const appendSearchValues = (target, value) => {
  if (Array.isArray(value)) {
    value.forEach((item) => appendSearchValues(target, item));
    return;
  }

  if (value && typeof value === "object") {
    [
      value.invoice_number,
      value.purchase_order_number,
      value.asn_number,
    ].forEach((item) => appendSearchValues(target, item));
    return;
  }

  const normalizedValue = normalizeText(value);
  if (normalizedValue) target.push(normalizedValue);
};

export const getShippingNoteSearchValues = (shippingNote = {}) => {
  const values = [];
  const asnId = Number(shippingNote.asn_id);

  [
    shippingNote.asn_number,
    Number.isInteger(asnId) && asnId > 0 ? asnId : "",
    Number.isInteger(asnId) && asnId > 0 ? `ASN-${asnId}` : "",
    shippingNote.invoice_numbers,
    shippingNote.purchase_order_numbers,
    shippingNote.invoices,
    shippingNote.purchase_orders,
  ].forEach((value) => appendSearchValues(values, value));

  return values;
};

export const sortShippingNotesByLatestEdd = (shippingNotes = []) =>
  [...(Array.isArray(shippingNotes) ? shippingNotes : [])].sort(
    (first, second) => {
      const firstDate = moment(first?.delivery_date);
      const secondDate = moment(second?.delivery_date);
      const firstTimestamp = firstDate.isValid() ? firstDate.valueOf() : 0;
      const secondTimestamp = secondDate.isValid() ? secondDate.valueOf() : 0;

      if (secondTimestamp !== firstTimestamp) {
        return secondTimestamp - firstTimestamp;
      }

      return Number(second?.asn_id || 0) - Number(first?.asn_id || 0);
    }
  );

export const filterShippingNotes = (
  shippingNotes = [],
  filterId = "dispatched",
  searchQuery = ""
) => {
  const selectedFilter =
    SHIPPING_NOTE_FILTERS.find((filter) => filter.id === filterId) ||
    SHIPPING_NOTE_FILTERS[0];
  const acceptedStatuses = new Set(selectedFilter.statuses);
  const normalizedQuery = normalizeText(searchQuery);

  return sortShippingNotesByLatestEdd(shippingNotes).filter((shippingNote) => {
    if (
      !acceptedStatuses.has(String(shippingNote?.status || "").toUpperCase())
    ) {
      return false;
    }

    return (
      !normalizedQuery ||
      getShippingNoteSearchValues(shippingNote).some((value) =>
        value.includes(normalizedQuery)
      )
    );
  });
};

export const getShippingNoteStatusCounts = (shippingNotes = []) =>
  SHIPPING_NOTE_FILTERS.reduce((counts, filter) => {
    counts[filter.id] = filterShippingNotes(shippingNotes, filter.id).length;
    return counts;
  }, {});

const formatMinutes = (value) => {
  const minutes = Number(value);
  if (!Number.isFinite(minutes)) return "";

  const normalizedMinutes = ((minutes % 1440) + 1440) % 1440;
  const hours = Math.floor(normalizedMinutes / 60);
  const minuteValue = Math.floor(normalizedMinutes % 60);
  const hour12 = hours % 12 || 12;
  const minuteLabel = minuteValue
    ? `:${String(minuteValue).padStart(2, "0")}`
    : "";

  return `${hour12}${minuteLabel}${hours < 12 ? "am" : "pm"}`;
};

export const formatShippingNoteSlot = (slot = {}) => {
  const startTime = formatMinutes(slot?.start_mins);
  const endTime = formatMinutes(slot?.end_mins);

  if (startTime && endTime) return `${startTime} - ${endTime}`;
  return typeof slot?.display_name === "string" && slot.display_name.trim()
    ? slot.display_name.trim()
    : "—";
};

export const getCourierDocumentNumber = (shippingNote = {}) => {
  const documents = Array.isArray(shippingNote?.asn_documents)
    ? shippingNote.asn_documents
    : [];
  const courierDocument = documents.find(
    (document) =>
      String(document?.document_type || "").toLowerCase() === "courier_doc"
  );

  return courierDocument?.document_number || "—";
};

export const getShippingNoteWarehouseCode = (shippingNote = {}) =>
  shippingNote?.warehouse?.warehouse_code || "—";

const normalizeDocumentType = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

const documentMatchesKind = (document, kind) => {
  const documentType = normalizeDocumentType(document?.document_type);

  if (kind === "eway") {
    return (
      documentType === "ewbdoc" ||
      documentType.includes("ewaybill") ||
      documentType.startsWith("ewb")
    );
  }

  if (kind === "courier") {
    return documentType.includes("courier") || documentType.includes("docket");
  }

  if (kind === "asn") {
    return (
      documentType === "asn" ||
      documentType === "asndoc" ||
      documentType === "asndocument" ||
      documentType.includes("advanceshippingnote") ||
      documentType.includes("shippingnotedoc")
    );
  }

  return false;
};

export const getShippingNoteDocument = (shippingNote = {}, kind) => {
  const documents = Array.isArray(shippingNote?.asn_documents)
    ? shippingNote.asn_documents
    : [];

  return (
    documents.find(
      (document) =>
        Boolean(String(document?.document_path || "").trim()) &&
        documentMatchesKind(document, kind)
    ) || null
  );
};

export const getShippingNoteMapUrl = (shippingNote = {}) => {
  return buildGoogleMapsUrl(
    shippingNote?.warehouse?.latitude,
    shippingNote?.warehouse?.longitude
  );
};

export const isPartiallyFulfilledShippingNote = (status) =>
  String(status || "").toUpperCase() === "PARTIALLY_FULFILLED";

export const isFulfilledShippingNote = (status) => {
  const normalizedStatus = String(status || "").toUpperCase();
  return (
    normalizedStatus === "FULFILLED" ||
    normalizedStatus === "PARTIALLY_FULFILLED"
  );
};

export const hasInvoiceGrn = (invoice = {}) => {
  const grnId = Number(invoice?.po_grn_mapping_id);
  return Number.isInteger(grnId) && grnId > 0;
};
